import React, { useState, useEffect, useRef } from 'react'
import './RecipeForm.css'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { 
  X, 
  ChevronDown, 
  ChevronUp,
  Save,
  FileText,
  UtensilsCrossed,
  ListOrdered,
  Tag,
  StickyNote,
  Image as ImageIcon,
  Sparkles,
  Check,
  Camera,
  Loader2,
  Upload
} from 'lucide-react'
import { RecipeFormData, RecipeCategory, Recipe, RecipeSource } from '@/types/recipe'
import { getAllTags } from '@/lib/supabase-service'
import { recipeContextManager } from '@/services/recipeContextService'
import { useConversationStore } from '@/stores/conversationStore'
import { generateImageDirectlyForRecipe } from '@/services/geminiService'
import { compressBase64Image } from '@/services/veniceService'
import { ErrorModal, useErrorModal } from '@/components/common/ErrorModal'

const CATEGORIES = [
  'Asian',
  'Sri Lankan',
  'Mexican',
  'Latin American',
  'Desserts',
  'Drinks',
  'Sauces',
  'Other',
  'To Try'
] as const

interface RecipeFormProps {
  onSubmit: (recipe: RecipeFormData) => Promise<void>
  id?: string
  initialData?: RecipeFormData
  isEditMode?: boolean
  onCancel?: () => void
  autoGenerateImage?: boolean
  forceDirectImageGeneration?: boolean
}

// Collapsible Section Component
interface SectionProps {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  badge?: string
  required?: boolean
  isComplete?: boolean
}

const Section: React.FC<SectionProps> = ({
  title,
  icon,
  children,
  defaultOpen = true,
  badge,
  required,
  isComplete
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors min-h-[56px]"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2 rounded-lg shrink-0 ${isComplete ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'}`}>
            {isComplete ? <Check className="h-4 w-4" /> : icon}
          </div>
          <div className="text-left min-w-0">
            <span className="font-semibold text-slate-900">{title}</span>
            {required && !isComplete && (
              <span className="ml-2 text-xs text-red-600">Required</span>
            )}
          </div>
          {badge && (
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full shrink-0">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  )
}

const EMPTY_FORM: RecipeFormData = {
  title: '',
  description: '',
  category: 'Asian',
  tags: [],
  ingredients: '',
  method: '',
  notes: '',
  image_url: undefined,
  has_custom_image: false,
  isFavorite: false,
  share: true,
  source: RecipeSource.User
}

const RecipeFormModern = ({
  onSubmit,
  initialData,
  id,
  isEditMode,
  onCancel,
  autoGenerateImage,
  forceDirectImageGeneration: _forceDirectImageGeneration
}: RecipeFormProps) => {
  const [formData, setFormData] = useState<RecipeFormData>(initialData || EMPTY_FORM)
  const [aiChangesNotification, setAiChangesNotification] = useState<string | null>(null)
  const [tagInput, setTagInput] = useState(initialData?.tags?.join(', ') || '')
  const [existingTags, setExistingTags] = useState<string[]>([])
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [inlineSuggestion, setInlineSuggestion] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const [autoImageComplete, setAutoImageComplete] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [image, setImage] = useState<string | undefined>(initialData?.image_url)
  const [imagePreview, setImagePreview] = useState<string | null>(initialData?.image_url || null)
  
  const { showError, modalProps: errorModalProps } = useErrorModal()
  useConversationStore()

  // Completion checks for visual feedback
  const isTitleComplete = formData.title.trim().length > 0
  const isDescriptionComplete = formData.description.trim().length > 0
  const isIngredientsComplete = formData.ingredients.trim().length > 0
  const isMethodComplete = formData.method.trim().length > 0

  // Load existing tags
  useEffect(() => {
    getAllTags().then(tags => {
      setExistingTags(tags)
    }).catch(() => undefined)
  }, [])

  // Helper function to convert form data to Recipe object for assistant context
  const createRecipeFromFormData = (data: RecipeFormData): Recipe => {
    return {
      id: id || 'temp-recipe-' + Date.now(),
      title: data.title || 'Untitled Recipe',
      description: data.description || '',
      category: data.category,
      tags: data.tags || [],
      ingredients: data.ingredients || '',
      method: data.method || '',
      notes: data.notes || '',
      image_url: data.image_url,
      has_custom_image: data.has_custom_image || false,
      isFavorite: data.isFavorite || false,
      share: data.share !== undefined ? data.share : true,
      dateCreated: new Date().toISOString(),
      dateModified: new Date().toISOString(),
      user_id: '',
      isOwner: true
    }
  }

  // Set recipe context for assistant when form data changes
  /* eslint-disable react-hooks/exhaustive-deps -- intentional granular deps: sync context only on meaningful fields; createRecipeFromFormData is pure in its arg and id (id already in deps) */
  useEffect(() => {
    if (formData.title && formData.title.trim()) {
      const contextRecipe = createRecipeFromFormData(formData)
      recipeContextManager.setCurrentRecipe(contextRecipe)
    } else {
      recipeContextManager.setCurrentRecipe(null)
    }
  }, [formData.title, formData.ingredients, formData.method, formData.description, id])
  /* eslint-enable react-hooks/exhaustive-deps */

  // Listen for image updates from assistant
  /* eslint-disable react-hooks/exhaustive-deps -- listener registration keyed on id + title; handleInputChange is intentionally unstable (writes to latest formData) */
  useEffect(() => {
    const handleImageUpdate = (event: CustomEvent) => {
      const { recipeId, imageData } = event.detail
      const currentContextRecipe = recipeContextManager.getCurrentRecipe()
      const isOurRecipe = !id || recipeId.startsWith('temp-recipe-') || 
                         (currentContextRecipe && currentContextRecipe.title === formData.title)
      
      if (isOurRecipe) {
        setImage(imageData)
        setImagePreview(imageData)
        handleInputChange('image_url', imageData)
        handleInputChange('has_custom_image', true)
      }
    }

    window.addEventListener('recipeImageUpdated', handleImageUpdate as EventListener)
    return () => {
      window.removeEventListener('recipeImageUpdated', handleImageUpdate as EventListener)
    }
  }, [id, formData.title])
  /* eslint-enable react-hooks/exhaustive-deps */

  // Listen for form updates from AI modifications
  useEffect(() => {
    const handleFormUpdate = (event: CustomEvent) => {
      const { modifiedFormData, changesSummary } = event.detail
      const originalData = { ...formData }
      
      const timestamp = new Date().toLocaleDateString()
      const changeNote = `AI Modified ${timestamp}: ${changesSummary.split('.')[0]}.`
      const existingNotes = formData.notes || ''
      const updatedNotes = existingNotes
        ? `${changeNote}\n-----------------\n${existingNotes}`
        : changeNote
      
      setFormData(prevData => ({
        ...prevData,
        ...modifiedFormData,
        notes: updatedNotes
      }))
      
      setAiChangesNotification(changesSummary)
      
      setTimeout(() => {
        const fieldsToCheck: (keyof RecipeFormData)[] = ['title', 'description', 'ingredients', 'method']
        fieldsToCheck.forEach(field => {
          if (originalData[field] !== modifiedFormData[field]) {
            const fieldElement = document.querySelector(`[name="${field}"], #${field}`)
            if (fieldElement) {
              fieldElement.classList.add('text-changed')
              setTimeout(() => {
                fieldElement.classList.remove('text-changed')
              }, 5000)
            }
          }
        })
      }, 100)
    }

    window.addEventListener('recipeFormUpdated', handleFormUpdate as EventListener)
    return () => {
      window.removeEventListener('recipeFormUpdated', handleFormUpdate as EventListener)
    }
  }, [formData])

  // Clean up context when component unmounts
  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname
      const isNavigatingToRecipe = currentPath.startsWith('/recipe/') && currentPath !== '/recipe/new'
      
      if (!isNavigatingToRecipe) {
        recipeContextManager.setCurrentRecipe(null)
      }
    }
  }, [])

  useEffect(() => {
    if (initialData) {
      setFormData(initialData)
      setTagInput(initialData.tags?.join(', ') || '')
      setImage(initialData.image_url || undefined)
      setImagePreview(initialData.image_url || null)
    } else {
      setFormData(EMPTY_FORM)
      setTagInput('')
      setImage(undefined)
      setImagePreview(null)
    }
  }, [initialData])

  // Auto-generation effect for Studio-created recipes
  /* eslint-disable react-hooks/exhaustive-deps -- intentional granular deps; handleAutoImageGeneration reads latest formData via closure so re-keying on it would loop */
  useEffect(() => {
    if (autoGenerateImage && formData.title && formData.ingredients && !autoImageComplete) {
      if (!(formData as any).studioGeneratedImage) {
        handleAutoImageGeneration()
      } else {
        setAutoImageComplete(true)
      }
    }
  }, [autoGenerateImage, formData.title, formData.ingredients, autoImageComplete])
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleAutoImageGeneration = async () => {
    if (!formData.title || !formData.ingredients) return

    setIsGeneratingImage(true)
    try {
      const tempRecipe: Recipe = {
        id: 'temp-recipe-auto-gen',
        title: formData.title,
        description: formData.description || '',
        category: formData.category,
        ingredients: formData.ingredients,
        method: formData.method || '',
        notes: formData.notes || '',
        tags: formData.tags || [],
        dateCreated: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        isFavorite: false,
        share: true,
        image_url: '',
        has_custom_image: false,
        user_id: ''
      }

      const result = await generateImageDirectlyForRecipe(tempRecipe)
      
      if (result.success && result.imageData) {
        setImage(result.imageData)
        setImagePreview(result.imageData)
        setAutoImageComplete(true)
      }
    } catch {
    } finally {
      setIsGeneratingImage(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      // Strip UI-only hand-off flags so they never reach the DB payload
      // (unknown columns make PostgREST reject the insert with 400/PGRST204).
      const {
        autoGenerateImage: _autoGenerateImage,
        fromStudio: _fromStudio,
        studioPersona: _studioPersona,
        studioGeneratedImage: _studioGeneratedImage,
        fromAssistant: _fromAssistant,
        ...cleanFormData
      } = formData as any
      
      const submitData: RecipeFormData = {
        ...cleanFormData,
        image_url: image,
        has_custom_image: !!image,
        tags: formData.tags.filter(tag => tag.trim() !== '')
      }
      await onSubmit(submitData)
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to submit recipe', 'Recipe Error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (
    field: keyof RecipeFormData,
    value: string | string[] | boolean | RecipeCategory | undefined
  ) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value }
      
      if (['title', 'ingredients', 'method', 'description'].includes(field) && newData.title && newData.title.trim()) {
        const contextRecipe = createRecipeFromFormData(newData)
        recipeContextManager.setCurrentRecipe(contextRecipe)
      }
      
      return newData
    })
  }

  const handleTagsChange = (value: string) => {
    setTagInput(value)
    
    const tags = value.split(',')
    const currentTag = tags[tags.length - 1].trim().toLowerCase()
    
    if (currentTag) {
      const suggestions = existingTags.filter(tag => 
        tag.toLowerCase().startsWith(currentTag) && 
        !tags.slice(0, -1).map(t => t.trim()).includes(tag)
      )
      if (suggestions.length > 0) {
        setInlineSuggestion(suggestions[0])
      } else {
        setInlineSuggestion('')
      }
      setTagSuggestions(suggestions)
      setShowSuggestions(true)
    } else {
      setInlineSuggestion('')
      setTagSuggestions([])
      setShowSuggestions(false)
    }

    const newTags = value.split(',').map(tag => tag.trim()).filter(tag => tag !== '')
    setFormData(prev => ({ ...prev, tags: newTags }))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && inlineSuggestion) {
      e.preventDefault()
      const tags = tagInput.split(',')
      const currentTag = tags[tags.length - 1].trim().toLowerCase()
      const suggestionSuffix = inlineSuggestion.slice(currentTag.length)
      if (suggestionSuffix) {
        const newTags = [...tags.slice(0, -1), inlineSuggestion].join(', ')
        setTagInput(newTags)
        handleTagsChange(newTags)
      }
    }
  }

  const handleTagSelect = (tag: string) => {
    const tags = tagInput.split(',').map(t => t.trim()).filter(t => t !== '')
    const newTags = [...new Set([...tags.filter(t => t !== tag), tag])]
    const newTagInput = newTags.join(', ')
    setTagInput(newTagInput)
    setFormData(prev => ({ ...prev, tags: newTags }))
    setShowSuggestions(false)
    tagInputRef.current?.focus()
  }

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setIsCompressing(true)
      try {
        const reader = new FileReader()
        reader.onloadend = async () => {
          let result = reader.result as string
          const sizeKB = (result.length * 0.75) / 1024
          
          if (sizeKB > 125) {
            try {
              result = await compressBase64Image(result)
            } catch {
            }
          }
          
          setImagePreview(result)
          setImage(result)
          setIsCompressing(false)
        }
        reader.onerror = () => {
          setIsCompressing(false)
        }
        reader.readAsDataURL(file)
      } catch {
        setIsCompressing(false)
      }
    }
  }

  const handleGenerateImage = async () => {
    setIsGeneratingImage(true)
    try {
      const tempRecipe = createRecipeFromFormData(formData)
      const result = await generateImageDirectlyForRecipe(tempRecipe)
      
      if (result.success && result.imageData) {
        setImage(result.imageData)
        setImagePreview(result.imageData)
        setAutoImageComplete(true)
      } else {
        showError(result.error || 'Image generation failed.', 'Image Generation Error')
      }
    } catch {
    } finally {
      setIsGeneratingImage(false)
    }
  }

  return (
    <form id={id} onSubmit={handleSubmit} className="max-w-3xl mx-auto" data-recipe-form>
      {/* AI Changes Notification */}
      {aiChangesNotification && (
        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <span className="font-medium text-purple-800">AI Modified Your Recipe</span>
            </div>
            <button
              type="button"
              onClick={() => setAiChangesNotification(null)}
              className="text-purple-600 hover:text-purple-800"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-purple-700 mt-1">{aiChangesNotification}</p>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white/95 glass-ios border-b border-slate-200 -mx-4 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <Input
              id="title"
              value={formData.title}
              onChange={e => handleInputChange('title', e.target.value)}
              placeholder="Recipe Title"
              required
              className="text-xl font-bold border-0 bg-transparent focus:ring-0 px-0 placeholder:text-slate-400"
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancel}
                disabled={isSubmitting}
                className="min-h-[40px]"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting || !isTitleComplete}
              className="bg-green-600 hover:bg-green-700 text-white shadow-md min-h-[44px]"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Update' : 'Save'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Hidden file input for image upload (referenced by Upload Photo / Replace buttons) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
        aria-hidden="true"
      />

      {/* Hero Image Section - Prominent at top */}
      <div className="mt-4 -mx-4 px-4">
        {isCompressing ? (
          <div className="aspect-[16/9] rounded-xl bg-slate-100 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-green-600 mx-auto mb-2" />
              <p className="text-sm text-slate-600">Optimizing image...</p>
            </div>
          </div>
        ) : isGeneratingImage ? (
          <div className="aspect-[16/9] rounded-xl bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center border-2 border-purple-200">
            <div className="text-center">
              <div className="relative mb-3">
                <div className="animate-spin h-10 w-10 border-[3px] border-purple-600 border-t-transparent rounded-full mx-auto" />
                <Sparkles className="h-5 w-5 text-purple-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="text-sm font-medium text-purple-700">Generating AI image...</p>
              <p className="text-xs text-purple-500 mt-1">This takes a moment</p>
            </div>
          </div>
        ) : imagePreview ? (
          <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-slate-100 group">
            <img
              src={imagePreview}
              alt="Recipe preview"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                className="bg-white/90 hover:bg-white text-xs"
              >
                <Camera className="h-3 w-3 mr-1" />
                Replace
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={handleGenerateImage}
                disabled={!formData.title || !formData.ingredients}
                className="bg-white/90 hover:bg-white text-xs"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Regenerate
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => {
                  setImagePreview(null)
                  setImage(undefined)
                  if (fileInputRef.current) fileInputRef.current.value = ''
                }}
                className="text-xs"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            {/* Image complete badge */}
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/90 text-white text-xs font-medium">
                <Check className="h-3 w-3" />
                Image ready
              </span>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 hover:border-purple-400 transition-colors">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <ImageIcon className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-sm font-medium text-slate-900 mb-1">Add a recipe image</h3>
              <p className="text-xs text-slate-500 mb-4">Make your recipe stand out with a photo</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full sm:w-auto"
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Upload Photo
                </Button>
                <span className="text-slate-400 text-xs hidden sm:inline">or</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleGenerateImage}
                  disabled={!formData.title || !formData.ingredients}
                  className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Sparkles className="h-3 w-3 mr-1" />
                  Generate with AI
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Sections - Compact */}
      <div className="space-y-3 pb-8 mt-4">
        
        {/* Description & Category */}
        <Section
          title="Description & Category"
          icon={<FileText className="h-4 w-4" />}
          required
          isComplete={isDescriptionComplete}
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                What makes this recipe special?
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                placeholder="A brief, appetizing description of your dish..."
                className="mt-1.5 min-h-[60px]"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Category
              </Label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleInputChange('category', cat as RecipeCategory)}
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-all min-h-[40px] ${
                      formData.category === cat
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Ingredients */}
        <Section
          title="Ingredients"
          icon={<UtensilsCrossed className="h-4 w-4" />}
          required
          isComplete={isIngredientsComplete}
          badge={formData.ingredients ? `${formData.ingredients.split('\n').filter(l => l.trim()).length} items` : undefined}
        >
          <Textarea
            id="ingredients"
            value={formData.ingredients}
            onChange={e => handleInputChange('ingredients', e.target.value)}
            placeholder="List your ingredients, one per line.

Example:
2 cups all-purpose flour
1 tsp salt
3 large eggs

FOR THE SAUCE:
1 cup heavy cream
2 tbsp butter"
            className="font-mono text-sm min-h-[150px]"
          />
          <p className="text-xs text-slate-500 mt-2">
            Tip: Use ALL CAPS for section headers (e.g., "FOR THE SAUCE:")
          </p>
        </Section>

        {/* Method */}
        <Section
          title="Method / Instructions"
          icon={<ListOrdered className="h-4 w-4" />}
          required
          isComplete={isMethodComplete}
          badge={formData.method ? `${formData.method.split('\n').filter(l => l.trim() && /^\d/.test(l.trim())).length} steps` : undefined}
        >
          <Textarea
            id="method"
            value={formData.method}
            onChange={e => handleInputChange('method', e.target.value)}
            placeholder="Write your cooking instructions.

Example:
1. Preheat oven to 350°F (175°C)
2. Mix dry ingredients in a large bowl
3. Add wet ingredients and stir until combined

BAKING:
1. Pour batter into prepared pan
2. Bake for 25-30 minutes"
            className="font-mono text-sm min-h-[150px]"
          />
          <p className="text-xs text-slate-500 mt-2">
            Tip: Number your steps and use ALL CAPS for section headers
          </p>
        </Section>

        {/* Tags */}
        <Section
          title="Tags"
          icon={<Tag className="h-4 w-4" />}
          defaultOpen={false}
          badge={formData.tags.length > 0 ? `${formData.tags.length} tags` : undefined}
        >
          <div className="relative">
            <Input
              ref={tagInputRef}
              id="tags"
              value={tagInput}
              onChange={e => handleTagsChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                setTimeout(() => {
                  setShowSuggestions(false)
                  setInlineSuggestion('')
                }, 200)
              }}
              onFocus={() => handleTagsChange(tagInput)}
              placeholder="quick, vegetarian, spicy, easy..."
            />
            {showSuggestions && tagSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {tagSuggestions.map((tag, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                    onClick={() => handleTagSelect(tag)}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Separate tags with commas • Press Tab to accept suggestion
          </p>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {formData.tags.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-sm text-slate-700"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => {
                      const newTags = formData.tags.filter((_, idx) => idx !== i)
                      setFormData(prev => ({ ...prev, tags: newTags }))
                      setTagInput(newTags.join(', '))
                    }}
                    className="hover:text-red-600"
                    aria-label={`Remove tag ${tag}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </Section>

        {/* Notes */}
        <Section 
          title="Notes & Tips" 
          icon={<StickyNote className="h-4 w-4" />}
          defaultOpen={false}
          isComplete={!!formData.notes?.trim()}
        >
          <Textarea
            id="notes"
            value={formData.notes || ''}
            onChange={e => handleInputChange('notes', e.target.value)}
            placeholder="Add any tips, variations, storage instructions, or helpful notes..."
            className="min-h-[80px]"
          />
        </Section>

      </div>
      
      <ErrorModal {...errorModalProps} />
    </form>
  )
}

export default RecipeFormModern
